import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { TrendingUp, TrendingDown, Minus, Activity } from "lucide-react";
import { AlertButton } from "./AlertButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const REFRESH_MS = 60000;
const hhmm = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: false }) : "--:--");

function Quote({ r }) {
  const up = r.trend === "up", down = r.trend === "down";
  const color = up ? "#2FB999" : down ? "#E07B72" : "#8A8A82";
  const Icon = up ? TrendingUp : down ? TrendingDown : Minus;
  const prev = useRef(r.price);
  const [pulse, setPulse] = useState(false);
  useEffect(() => {
    if (prev.current !== r.price) { setPulse(true); prev.current = r.price; const t = setTimeout(() => setPulse(false), 1900); return () => clearTimeout(t); }
  }, [r.price]);
  return (
    <span className="inline-flex items-center gap-2 px-6 whitespace-nowrap" data-testid={r._dup ? undefined : `ticker-${r.category}`} aria-hidden={r._dup || undefined} data-pulse={pulse}>
      <span className="font-grotesk text-[10px] uppercase tracking-[0.22em] text-[#C9C9C2]">{r.label}</span>
      <span className={`font-serif text-sm text-[#F5F5F0] ${pulse ? "gold-price-pulse" : ""}`} data-testid={r._dup ? undefined : `ticker-price-${r.category}`}>₹{Number(r.price).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</span>
      <span className="font-grotesk text-[9px] text-[#8A8A82]">/kg</span>
      <span className="inline-flex items-center gap-1 font-grotesk text-[10px]" style={{ color }}>
        <Icon size={11} />{r.change_pct > 0 ? "+" : ""}{Number(r.change_pct).toFixed(2)}%
      </span>
      {!r._dup && <AlertButton compact category={r.category} label={r.label} unit={r.unit} price={r.price} color="#C9A961" />}
      <span className="text-[#B87333]/50 pl-4">◆</span>
    </span>
  );
}

export const MetalTicker = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    const load = () => axios.get(`${API}/rates/metal-index`).then((r) => alive && setData(r.data)).catch(() => {});
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);
  if (!data || !data.rows?.length) return null;
  const strip = [...data.rows, ...data.rows.map((r) => ({ ...r, _dup: true }))];
  return (
    <div className="relative border-y border-[#B87333]/25 bg-[#0B0B0B] overflow-hidden" data-testid="metal-ticker">
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center gap-2 pl-6 md:pl-12 pr-6 bg-[#0B0B0B] border-r border-[#B87333]/25" data-testid="metal-ticker-label">
        <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#B87333] opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-[#B87333]" /></span>
        <Activity size={12} className="text-[#B87333]" />
        <Link to="/trends" className="font-grotesk text-[10px] uppercase tracking-[0.28em] text-[#B87333] hover:text-[#F5F5F0] transition-colors">Vinci Metal Index</Link>
      </div>
      <div className="absolute right-0 top-0 bottom-0 z-10 hidden md:flex items-center pl-6 pr-6 md:pr-12 bg-[#0B0B0B] border-l border-[#B87333]/25 font-grotesk text-[9px] uppercase tracking-[0.2em] text-[#8A8A82]" data-testid="metal-ticker-meta">
        LME / MCX-referenced · updated {hhmm(data.updated_at)}
      </div>
      <div className="py-2.5 pl-56 md:pl-64 overflow-hidden">
        <div className="flex w-max animate-[ticker_45s_linear_infinite] hover:[animation-play-state:paused]">
          {strip.map((r, i) => <Quote key={`${r.category}-${i}`} r={r} />)}
        </div>
      </div>
      <style>{`@keyframes ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }`}</style>
    </div>
  );
};
